import type * as rxjs from 'rxjs'

import type { InjectableDependencies } from '../injectable-dependencies'

import type { Action } from './action'
import type { Op } from './op'
import type { Source } from './source'

export interface Producer<TInputData, TAction extends Action.Constraint> {
  readonly create: SingletonMethod<TInputData, TAction>
  /**
   * Creates and dispatches an {@link Action}
   *
   * Returns the {@link Action} that was dispatched, in case you need
   * information about the {@link Action}, such as the {@link Action.traceId}
   */
  readonly dispatch: SingletonMethod<TInputData, TAction>
  readonly bulkCreate: (...dataList: readonly TInputData[]) => TAction[]
}
export namespace Producer {
  export function from<TInputData, TAction extends Action.Constraint>(
    injectables: InjectableDependencies,
    create: SingletonMethod<TInputData, TAction>,
  ): Producer<TInputData, TAction>
  export function from(
    injectables: InjectableDependencies,
    create: (data: unknown) => Action,
  ): Producer<unknown, Action> {
    return {
      bulkCreate: (...dataList: readonly unknown[]) =>
        dataList.map(data => create(data)),
      create,
      dispatch: (data: unknown) => {
        const action = create(data)
        injectables.store.dispatch(action)
        return action
      },
    }
  }

  export interface Factory<TInputData, TAction extends Action.Constraint> {
    /**
     * TODO: explain
     */
    readonly from: (
      source: Source,
      options?: Factory.from.Options,
    ) => Producer<TInputData, TAction>
  }
  export namespace Factory {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export namespace from {
      export interface Options
        extends Pick<
          Action.constructor.Params<any, Op, unknown>,
          'traceStash'
        > {}
    }
  }

  export interface WithExecute<
    TInputData,
    TAction extends Action.Constraint,
    TOutputData,
  > extends Producer<TInputData, TAction> {
    readonly execute: SingletonMethod<TInputData, ExecutionResult<TOutputData>>
  }
  export namespace WithExecute {
    export interface Factory<
      TInputData,
      TAction extends Action.Constraint,
      TOutputData,
    > {
      /**
       * TODO: explain
       */
      readonly from: (
        source: Source,
        options?: Factory.from.Options,
      ) => Producer.WithExecute<TInputData, TAction, TOutputData>
    }
  }

  export interface ExecutionResult<T> {
    readonly asPromise: Promise<T>
    readonly asObservable: rxjs.Observable<T>
  }
}

/**
 * Method that accepts a single data object
 *
 * Used to make the parameter optional if data can be undefined
 */
type SingletonMethod<TInputData, TRet> = [
  Extract<TInputData, undefined>,
] extends [never]
  ? (data: TInputData) => TRet
  : (data?: TInputData) => TRet
